import type { ApolloError } from 'apollo-client';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { usePageSpaceKey } from '@confluence/page-context';
import { getSuperAdminClaim } from '@confluence/super-admin-claim';
import { expVal } from '@confluence/feature-experiments';
import { getQueryParamsAndSetFetchPolicyForNetworkOnlyQuery } from '@confluence/query-preloader-tools/entry-points/getQueryParamsAndSetFetchPolicyForNetworkOnlyQuery';

import type { SpaceRestrictionCheckQuery as SpaceRestrictionCheckQueryType } from './__types__/SpaceRestrictionCheckQuery';
import { SpaceRestrictionCheckQuery } from './SpaceRestrictionCheckQuery.graphql';

type SpaceRestrictionCheckProps = {
	asSuperAdmin?: boolean;
	children: (result: {
		error?: ApolloError | undefined;
		isSpaceNotFound: boolean | undefined;
		isSpaceRestricted: boolean | undefined;
		loading?: boolean;
	}) => JSX.Element;
	spaceKey: string;
};

export function SpaceRestrictionCheck({
	asSuperAdmin,
	children,
	spaceKey,
}: SpaceRestrictionCheckProps) {
	const superAdminClaim = getSuperAdminClaim();
	const [stateSpaceKey] = usePageSpaceKey();

	let data, error, loading;
	if (expVal('confluence_restrictions_fix', 'isEnabled', false)) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const result = useQuery<SpaceRestrictionCheckQueryType>(
			...getQueryParamsAndSetFetchPolicyForNetworkOnlyQuery({
				query: SpaceRestrictionCheckQuery,
				context: asSuperAdmin == undefined ? undefined : { asSuperAdmin },
				errorPolicy: 'all',
				variables: {
					// XXX Though SpaceRestrictionCheckProps defines spaceKey as required,
					// SpaceBaseComponent is not written in TypeScript and it may provide
					// undefined as it is reading spaceKey from redux. While it would result
					// in a better separation of concerns to have a fix in SpaceBaseComponent,
					// such a fix would be riskier.
					spaceKey: spaceKey || stateSpaceKey!,
				},
			}),
		);
		data = result.data;
		error = result.error;
		loading = result.loading;
	} else {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const result = useQuery<SpaceRestrictionCheckQueryType>(
			// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations
			SpaceRestrictionCheckQuery,
			{
				context: asSuperAdmin == undefined ? undefined : { asSuperAdmin },
				fetchPolicy: superAdminClaim ? 'no-cache' : 'network-only',
				errorPolicy: 'all',
				variables: {
					// XXX Though SpaceRestrictionCheckProps defines spaceKey as required,
					// SpaceBaseComponent is not written in TypeScript and it may provide
					// undefined as it is reading spaceKey from redux. While it would result
					// in a better separation of concerns to have a fix in SpaceBaseComponent,
					// such a fix would be riskier.
					spaceKey: spaceKey || stateSpaceKey!,
				},
			},
		);
		data = result.data;
		error = result.error;
		loading = result.loading;
	}

	let isErrorHandled: boolean | undefined;
	let isSpaceNotFound: boolean | undefined;
	let isSpaceRestricted: boolean | undefined;

	if (error) {
		isSpaceNotFound = isSpaceNotFoundError(error);
		isSpaceRestricted = isSpaceRestrictedError(error);
		if (isSpaceNotFound || isSpaceRestricted) {
			markErrorAsHandled(error);
			isErrorHandled = true;
		}
	} else if (
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		process.env.REACT_SSR &&
		expVal('confluence_frontend_ssr_space_not_found', 'isEnabled', false)
	) {
		isSpaceNotFound = window['__SPACE_NOTFOUND_CHECK_ERROR__'];
		isSpaceRestricted = window['__SPACE_RESTRICTION_CHECK_ERROR__'];
		isErrorHandled =
			window['__SPACE_RESTRICTED_CHECK_ERROR__'] || window['__SPACE_NOTFOUND_CHECK_ERROR__'];
	} else if (data) {
		isSpaceNotFound = false;
		isSpaceRestricted = false;
	}

	if (expVal('confluence_restrictions_fix', 'isEnabled', false)) {
		if (loading) {
			return null;
		}
		return children({
			error,
			isSpaceNotFound,
			isSpaceRestricted,
		});
	} else {
		return children({
			error: isErrorHandled ? undefined : error,
			isSpaceNotFound,
			isSpaceRestricted,
			loading,
		});
	}
}

export function isSpaceNotFoundError(error: ApolloError) {
	return isSpaceError(error, ({ statusCode }) => statusCode === 404 || statusCode === 400);
}

export function isSpaceRestrictedError(error: ApolloError) {
	return isSpaceError(error, () => isUnauthorizedError(error));
}

function isSpaceError(
	{ graphQLErrors }: ApolloError,
	extensionsPredicate: (extensions: {
		data?: { authorized?: boolean };
		statusCode?: number;
	}) => boolean,
) {
	if (graphQLErrors && graphQLErrors.length > 0) {
		for (const { extensions, path } of graphQLErrors) {
			if (
				Array.isArray(path) &&
				path.indexOf('space') !== -1 &&
				extensions &&
				extensionsPredicate(extensions)
			) {
				return true;
			}
		}
	}
	return false;
}
